/*!
 * Copyright 2019 Fondazione Bruno Kessler
 * Licensed under the EUPL, Version 1.1 or - as soon they will be approved by the European
 * Commission - subsequent versions of the EUPL (the "Licence"); You may not use this work
 * except in compliance with the Licence.
 *
 * You may obtain a copy of the Licence at:
 * http://ec.europa.eu/idabc/eupl.html
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the Licence is distributed on an "AS IS" basis, WITHOUT WARRANTIES OR CONDITIONS OF
 * ANY KIND, either express or implied. See the Licence for the specific language
 * governing permissions and limitations under the Licence.
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  ViewChild,
} from "@angular/core";
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from "@angular/forms";
import {IonInput} from "@ionic/angular";
import {Drug} from "@trec/api/drug-store";

export const DRUG_SELECTOR_VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  useExisting: forwardRef(() => DrugSelectorComponent),
  multi: true,
};

@Component({
  selector: "c3-drug-selector",
  templateUrl: "./drug-selector.component.html",
  styleUrls: ["./drug-selector.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DRUG_SELECTOR_VALUE_ACCESSOR],
  standalone: false
})
export class DrugSelectorComponent implements ControlValueAccessor {
  // IO ///////////////////////////////////////////////////////////////////////////////////////////

  @Input()
  searchPending: boolean;

  @Input()
  searchResults: Drug[] | undefined;

  @Output()
  search: EventEmitter<string | null> = new EventEmitter();

  // DOM Elements /////////////////////////////////////////////////////////////////////////////////

  @ViewChild("inputField")
  inputElem: IonInput;

  // Form control value accessor //////////////////////////////////////////////////////////////////

  /**
   * Angular control's on change function.
   */
  onChange?: (data: Drug | null) => void;

  /**
   * Angular control's on touched function.
   */
  onTouched?: () => void;

  disabled = false;

  // Other variables //////////////////////////////////////////////////////////////////////////////

  /**
   * Value displayed in search drug field.
   */
  drugSearchValue = "";

  /**
   * Current vieweed and selected drug.
   */
  currentDrug?: Drug | null;

  constructor(private cd: ChangeDetectorRef) { }

  onDrugSelected(drug: Drug | null = null) {
    this.currentDrug = drug;
    this.triggerControlValueAccessorChange(drug);
    this.cd.markForCheck();
  }

  onDoSearch(value: string | Drug) {
    if (typeof value === "string") {
      // Search for drug
      this.search.emit(value);
    } else {
      // Drug has been selected
      this.onDrugSelected(value);
    }
  }

  public async setSearchValue(text: string): Promise<void> {
    const el = await this.inputElem.getInputElement();

    // Set new value -> trigger changes
    this.drugSearchValue = text;
    this.onDoSearch(text);
    // Trigger event to open autocomplete option list popup
    el.dispatchEvent(new Event("input"));
    this.cd.detectChanges();
  }

  // Control value accessor methods ///////////////////////////////////////////////////////////////

  writeValue(drug: Drug | null = null): void {
    this.currentDrug = drug;
    this.cd.markForCheck();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
    this.cd.markForCheck();
  }

  private triggerControlValueAccessorChange(data: Drug | null) {
    if (!this.onChange || !this.onTouched) {
      throw new Error("ControlValueAccessor still not initialized");
    }

    this.onTouched();
    this.onChange(data);
  }
}
